import { vfsWriteIfNotExists } from '../../vfs';
import type { FileRoot } from '../../vfs';
import type { LoggerFunction } from '../../types';
import { buildAppEnvVarName } from '../../internal-utils';

/**
 * Build the source for an API app's `api-component.ts` — the Lifecycleion
 * component that boots the Unirend API server, registers routes/page-data
 * handlers, and wires graceful start/stop.
 *
 * API-only and structurally unique (no SSG/SSR counterpart), so it lives in
 * `templates-specific/api/`. The component's own `name: 'api-server'` (passed
 * to `BaseComponent`) stays generic per the naming rule — only the manager name
 * in serve.ts incorporates the app name. The one per-project substitution is an
 * inline comment that names the build script (`${appName}:build`), kept
 * accurate so it matches whatever the generated app's scripts are called. The
 * `{{port}}` token is the Lifecycleion logger's own param syntax, not a
 * scaffold variable.
 *
 * @param appName - The app/project name, folded into the build-script comment
 */
function buildAPIComponentSrc(appName: string): string {
  const portEnvVarName = buildAppEnvVarName(appName, 'PORT');

  return `import { BaseComponent } from 'lifecycleion/lifecycle-manager';
import type { Logger } from 'lifecycleion/logger';
import { serveAPI, UnirendLifecycleionLoggerAdaptor } from 'unirend/server';
import type { APIServer } from 'unirend/server';
import { loadBuildInfo } from 'unirend/build-info';
import { cookies } from 'unirend/plugins';
// Uncomment when using a custom APIResponseHelpersClass.
// import { YourCustomAPIResponseHelpers } from './YourCustomAPIResponseHelpers';

// Read port from ${portEnvVarName} env var, default 3001.
// Production HTTPS: use a reverse proxy (nginx, Caddy, etc.) for TLS termination,
// or see https://github.com/keverw/unirend/blob/master/docs/https.md to handle it in code.
//
// Note: Unlike SSG/SSR servers, API servers typically don't need an HTTP→HTTPS redirect
// server (serveRedirect). API clients and data loaders should be configured to use the
// HTTPS URL directly and should not follow redirects. If using serveRedirect() (e.g. for
// a browser-facing root page or API documentation), set its targetPort to ${portEnvVarName} and
// use a separate HTTP_REDIRECT_PORT env var with a default. Then run both servers in the
// same component in parallel, or add a dedicated redirect component.
const PORT = parseInt(process.env['${portEnvVarName}'] ?? '3001', 10);

export class APIServerComponent extends BaseComponent {
  private server: APIServer | null = null;
  private startPromise: Promise<void> | null = null;
  // Stored so concurrent callers (e.g. onShutdownForce) join the same
  // in-flight promise rather than starting a second concurrent close.
  private stopPromise: Promise<void> | null = null;

  // Mutable sub-object held by reference inside publicAppConfig. Because
  // publicAppConfig is deep-cloned per request, mutating this object between
  // requests (e.g. at midnight) is picked up by all subsequent requests without restart.
  // See: https://github.com/keverw/unirend/blob/master/docs/ssr.md
  private readonly siteInfo = { current_year: new Date().getFullYear() };
  private midnightTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(parentLogger: Logger) {
    super(parentLogger, {
      name: 'api-server',
      // 30s graceful: gives the server time to drain in-flight requests and active
      // WebSocket connections before force-closing.
      shutdownGracefulTimeoutMS: 30_000,
      // 5s force: after closeAllConnections() kicks in, stop() should resolve almost
      // immediately — this is just a safety net for anything that still hangs.
      shutdownForceTimeoutMS: 5_000,
    });
  }

  public async start(): Promise<void> {
    // Starting while shutdown is active is not a safe no-op: the manager could
    // mark the component running while the old stop() is still draining.
    if (this.stopPromise) {
      throw new Error('Cannot start server while shutdown is in progress');
    }

    // Return the same promise if start is already running, so concurrent callers
    // join the in-flight operation instead of starting a second concurrent startup.
    if (this.startPromise) {
      return this.startPromise;
    }

    this.startPromise = (async () => {
      try {
        // Load build info generated by generate:build-info, which always runs first in ${appName}:build.
        // IS_BUILT is injected at bundle time via bun build --define 'IS_BUILT=true|false'.
        // When false or not injected, loadBuildInfo uses safe defaults without attempting
        // the import. See: https://github.com/keverw/unirend/blob/master/docs/build-info.md
        // @ts-expect-error IS_BUILT is a build-time constant injected via bun build --define; not declared in TypeScript source
        const isBuilt = typeof IS_BUILT !== 'undefined' && IS_BUILT === true;
        const buildResult = await loadBuildInfo(isBuilt, () => {
          // When bundling the server (e.g. bun build --target=node),
          // this is inlined at bundle time, unless externalized.
          return import('./current-build-info.ts');
        });

        // This level controls the adapter's gate — what Fastify passes to the Lifecycleion
        // logger. Set to 'debug' so everything gets through and the ConsoleSink's minLevel
        // does the real filtering in one place. 'trace' gives even more verbose Fastify
        // output but is treated as debug on the Lifecycleion logger side since there is no trace level.
        const loggingConfig = {
          logger: UnirendLifecycleionLoggerAdaptor(this.logger),
          level: 'debug' as const,
        };

        this.server = serveAPI({
          apiEndpoints: {
            // Set apiEndpointPrefix to false to disable API handling entirely
            // (e.g. when using as a plain web server with pluginHost.get/post routes).
            // In that mode, server.api.* and server.pageDataHandler.* helpers are
            // disabled and startup throws if any were registered, because those
            // helpers are envelope-first.
            apiEndpointPrefix: '/api',
            versioned: true,
            pageDataEndpoint: 'page_data',
          },
          logging: loggingConfig,
          // Safe-to-share config cloned and frozen per request as request.publicAppConfig.
          // API servers do not inject this into HTML (unlike SSR). Include selected values
          // in your own envelope meta, payload, or custom response handlers when a client
          // should receive them.
          // See: https://github.com/keverw/unirend/blob/master/docs/ssr.md
          publicAppConfig: {
            site_info: this.siteInfo,
            // Unlike SSR, publicAppConfig on the API server is NOT serialized into HTML or
            // window.__PUBLIC_APP_CONFIG__. It is available as request.publicAppConfig inside
            // page data handlers, API route handlers, and custom APIResponseHelpersClass methods —
            // include build info explicitly in any response envelope where you want clients to
            // see it, for example in API response envelope meta, a /health or /debug endpoint.
            // See: https://github.com/keverw/unirend/blob/master/docs/build-info.md
            build: {
              version: buildResult.info.version,
              git_hash: buildResult.info.git_hash,
              git_branch: buildResult.info.git_branch,
            },
          },
          // Client identity (request ID, real client IP, correlation ID, and the
          // X-Request-ID/X-Correlation-ID response headers) is resolved by the server on
          // every request. Trusting forwarded SSR headers (to recover the original client
          // across a separated SSR → API hop) is OFF by default — opt in with
          // clientInfo.trustForwardedHeaders: 'local'.
          // Configure or disable via the clientInfo option (and getConnectionIP / getRequestID).
          // See: https://github.com/keverw/unirend/blob/master/docs/client-identity.md
          // clientInfo: { /* trustForwardedHeaders, setResponseHeaders, ... */ },
          // clientInfo: false, // disable resolution entirely

          // Register server plugins. Unirend includes built-in plugins (like cors,
          // cookies, domainValidation, etc.) and supports creating your own custom plugins.
          // See: https://github.com/keverw/unirend/blob/master/docs/built-in-plugins.md (Built-in)
          // See: https://github.com/keverw/unirend/blob/master/docs/server-plugins.md (Custom)
          plugins: [cookies()],

          // ─── Error / 404 handlers ────────────────────────────────────────────────
          // Note: Unlike SSR servers which use APIHandling: { errorHandler, notFoundHandler },
          // the API server accepts these as top-level options.
          //
          // Both support two forms:
          //   - Function form: returns a JSON envelope in API mode, or WebResponse when
          //     apiEndpoints.apiEndpointPrefix is false (plain web server mode).
          //   - Split form: { api, web } for mixed servers (API server only). Requests matching
          //     apiEndpoints.apiEndpointPrefix use the 'api' handler, others use the 'web' handler.
          //
          // When omitted, Unirend uses sensible defaults. Convention: include
          // errorDetails: isDevelopment ? { stack: error.stack } : undefined for dev stack traces.
          // API/page error, not-found, and closing handlers receive
          // params.APIResponseHelpers, the configured helper class for this server.
          // Split-form web handlers do not receive params because they return WebResponse.
          // See: https://github.com/keverw/unirend/blob/master/docs/ssr.md (Standalone API section)
          //
          // errorHandler: (request, error, isDevelopment, isPageData, params) => {
          //   // isPageData distinguishes page data requests from regular API requests.
          //   // Use params.APIResponseHelpers.createAPIErrorResponse for API requests
          //   // or params.APIResponseHelpers.createPageErrorResponse for page data requests.
          //   if (isPageData) {
          //     return params.APIResponseHelpers.createPageErrorResponse({
          //       request,
          //       statusCode: 500,
          //       errorCode: 'internal_server_error',
          //       errorMessage: isDevelopment ? error.message : 'Internal server error',
          //       pageMetadata: {
          //         title: 'Server Error',
          //         description: 'An internal server error occurred',
          //       },
          //       errorDetails: isDevelopment ? { stack: error.stack } : undefined,
          //     });
          //   }

          //   return params.APIResponseHelpers.createAPIErrorResponse({
          //     request,
          //     statusCode: 500,
          //     errorCode: 'internal_server_error',
          //     errorMessage: isDevelopment ? error.message : 'Internal server error',
          //     errorDetails: isDevelopment ? { stack: error.stack } : undefined,
          //   });
          // },
          //
          // notFoundHandler: (request, isPageData, params) => {
          //   if (isPageData) {
          //     return params.APIResponseHelpers.createPageErrorResponse({
          //       request,
          //       statusCode: 404,
          //       errorCode: 'not_found',
          //       errorMessage: \`Page data endpoint not found: \${request.url}\`,
          //       pageMetadata: {
          //         title: 'Not Found',
          //         description: 'The requested page data could not be found',
          //       },
          //     });
          //   }

          //   return params.APIResponseHelpers.createAPIErrorResponse({
          //     request,
          //     statusCode: 404,
          //     errorCode: 'not_found',
          //     errorMessage: \`Endpoint not found: \${request.url}\`,
          //   });
          // },

          // ─── Closing handler ────────────────────────────────────────────────────
          // Customize the response returned to clients that arrive while the server
          // is shutting down. When omitted, Unirend sends a default 503 response.
          //
          // Function form handles API requests. Split form separates API and web handlers
          // for mixed servers (when serving both API routes and web pages on the same port).
          // Missing handlers fall back to the default 503 response.
          // See: https://github.com/keverw/unirend/blob/master/docs/ssr.md
          //
          // closingHandler: (request, isPageData, params) => {
          //   return params.APIResponseHelpers.createAPIErrorResponse({
          //     request,
          //     statusCode: 503,
          //     errorCode: 'service_unavailable',
          //     errorMessage: 'Server is shutting down. Please try again shortly.',
          //   });
          // },
          //
          // Split form (when mixing API and web routes on the same port):
          // closingHandler: {
          //   api: (request, isPageData, params) => {
          //     return params.APIResponseHelpers.createAPIErrorResponse({
          //       request,
          //       statusCode: 503,
          //       errorCode: 'service_unavailable',
          //       errorMessage: 'Server is shutting down. Please try again shortly.',
          //     });
          //   },
          //   web: (_request) => ({
          //     contentType: 'html',
          //     content: '<html><body>Server shutting down. Try again shortly.</body></html>',
          //     statusCode: 503,
          //   }),
          // },

          // ─── WebSocket support ───────────────────────────────────────────────────
          // Uncomment to enable WebSockets on the API server.
          // Bun caveat: preClose and preValidation hooks may hang when run directly under Bun.
          // We recommend bundling with Bun (targeting Node) and running the build under Node.
          // See: https://github.com/keverw/unirend/blob/master/README.md#toolchain-recommendation
          // and https://github.com/keverw/unirend/blob/master/docs/websockets.md
          //
          // enableWebSockets: true,
          // webSocketOptions: {
          //   perMessageDeflate: false,
          //   maxPayload: 100 * 1024 * 1024,
          //   preClose: async (clients) => {
          //     // Broadcast a shutdown event and/or close all connections gracefully
          //     // before the server stops listening:
          //     for (const client of clients) {
          //       (client as import('ws').WebSocket).close(1001, 'Server shutting down');
          //     }
          //
          //     // A brief delay allows clients to receive the close frames/messages:
          //     await new Promise((r) => setTimeout(r, 500));
          //   },
          // },

          // ─── File uploads ────────────────────────────────────────────────────────
          // Enable multipart file upload support with global limits.
          // Per-route limits are set via processFileUpload() in individual handlers.
          // See: https://github.com/keverw/unirend/blob/master/docs/file-upload-helpers.md
          //
          // fileUploads: {
          //   enabled: true,
          //   limits: { fileSize: 10 * 1024 * 1024, files: 10, fields: 10, fieldSize: 1024 },
          //   // Important: when versioned: true (the default), allowedRoutes must include
          //   // the version prefix (e.g. '/api/v1/upload/avatar' not '/api/upload/avatar').
          //   allowedRoutes: ['/api/v1/upload/avatar'],
          // },

          // Extend APIResponseHelpers to inject shared metadata (e.g. auth state, user
          // info) into every envelope response, then pass your subclass here so all routes
          // — including 404s — get it consistently.
          // See: https://github.com/keverw/unirend/blob/master/docs/api-envelope-structure.md
          // See: https://github.com/keverw/unirend/blob/master/docs/data-loaders.md
          // APIResponseHelpersClass: YourCustomAPIResponseHelpers,
        });

        // Start the midnight timer so siteInfo.current_year stays current.
        this.scheduleMidnightUpdate();

        // Register data loaders and API routes
        this.registerRoutes(this.server);

        // ─── WebSocket handlers route registration ──────────────────────────────────────
        // Uncomment after enabling enableWebSockets above.
        //
        // this.server.registerWebSocketHandler({
        //   path: '/ws/echo',
        //   preValidate: (request, params) => {
        //     // Validate the connection before upgrading:
        //     // - Upgrade: return { action: 'upgrade', data: { ... } } where any custom
        //     //   data returned is passed to the handler as the fourth argument (upgradeData).
        //     // - Reject: return { action: 'reject', envelope } to prevent the upgrade and
        //     //   send the APIResponseEnvelope instead. Use params.APIResponseHelpers to
        //     //   construct the envelope, ensuring custom helper subclasses configured on the
        //     //   server are respected.
        //     const token = params.queryParams['token'] as string;
        //
        //     if (token !== 'secret') {
        //       return {
        //         action: 'reject',
        //         envelope: params.APIResponseHelpers.createAPIErrorResponse({
        //           request,
        //           statusCode: 401,
        //           errorCode: 'invalid_token',
        //           errorMessage: 'Missing or invalid token',
        //         }),
        //       };
        //     }
        //
        //     return { action: 'upgrade', data: { authenticated: true } };
        //   },
        //   handler: (socket, request, params, upgradeData) => {
        //     // The handler receives the active socket connection, the original Fastify request,
        //     // pre-extracted routing params, and any upgradeData returned from preValidate.
        //     // The socket parameter is a standard 'ws' WebSocket instance.
        //     socket.send(JSON.stringify({ type: 'welcome', upgradeData }));
        //
        //     socket.on('message', (msg) => {
        //       socket.send(\`Echo: \${msg.toString()}\`);
        //     });
        //
        //     // Note that error events should be handled on the socket to prevent crashes.
        //     // The connection will automatically close after an error is received.
        //     socket.on('error', (err) => console.error('WebSocket error:', err));
        //     socket.on('close', (code, reason) => {
        //       console.log(\`WebSocket disconnected: \${code} \${reason}\`);
        //     });
        //   },
        // });

        // Start listening for requests
        await this.server.listen(PORT, '0.0.0.0');

        this.logger.success('API server running at http://localhost:{{port}}', {
          params: { port: PORT },
        });
      } catch (error) {
        // Reset promises and references on failure so that startup can be retried.
        // We throw the error so it propagates to the caller.
        this.startPromise = null;
        this.server = null;
        if (this.midnightTimer !== null) {
          clearTimeout(this.midnightTimer);
          this.midnightTimer = null;
        }
        throw error;
      }
    })();

    return this.startPromise;
  }

  public async stop(): Promise<void> {
    // Return the same promise if stop is already running, so concurrent callers
    // (including onShutdownForce) join the in-flight operation
    // instead of starting a second concurrent close.
    if (this.stopPromise) {
      return this.stopPromise;
    }

    this.stopPromise = (async () => {
      try {
        // Await active startup to settle before stopping, preventing orphaned listening
        // sockets if shutdown is initiated mid-boot. If startup hangs, the manager's
        // shutdown timeouts or process termination will clean it up.
        if (this.startPromise) {
          try {
            await this.startPromise;
          } catch {
            // Ignore start errors since we are stopping anyway
          }
        }

        // Stop the server if it successfully started and is listening. Keep a
        // local reference so the callback stops the same server instance even if
        // component state changes while shutdown is in progress.
        const server = this.server;
        if (server?.isListening()) {
          await server.stop();
        }

        // Only clear the server reference after a successful stop. If stop()
        // rejects, force shutdown still needs this.server to close connections.
        this.server = null;
        this.startPromise = null;

        // Cancel the midnight year-update timer so it doesn't fire after shutdown.
        if (this.midnightTimer !== null) {
          clearTimeout(this.midnightTimer);
          this.midnightTimer = null;
        }
      } finally {
        // Runs on both success and error. Without this, a thrown error would leave
        // stopPromise pointing at a rejected promise forever. Since there's no catch,
        // errors still propagate normally to any caller awaiting this promise.
        this.stopPromise = null;
      }
    })();

    return this.stopPromise;
  }

  public async onShutdownForce(): Promise<void> {
    // Force-close open connections so server.stop() can finish draining and resolve.
    // The outer ?. handles server not yet assigned (e.g. start() failed) — the inner ?.
    // handles runtimes that don't expose closeAllConnections.
    this.server?.closeAllConnections?.();

    // Join the original stop() — won't start a second close.
    await this.stop();
  }

  // Exposes this component's health status. LifecycleManager uses this internally
  // when checking the overall system health (e.g., printed on SIGUSR1 or exposed
  // via a dedicated health router). If the server is not started yet or has stopped,
  // this naturally returns unhealthy.
  //
  // Production tip: For orchestrators (Kubernetes, ECS, etc.), you can either probe
  // a route on the main port (like the GET /health endpoint below) or run a dedicated
  // internal health check server on a separate port (e.g., 9000) that returns HTTP 200/503
  // based on this healthCheck() result to avoid exposing orchestrator traffic on the main port.
  public healthCheck() {
    if (!this.server) {
      return {
        healthy: false,
        message: 'Server is not started',
      };
    }

    const isHealthy = this.server.isListening();
    return {
      healthy: isHealthy,
      message: isHealthy
        ? \`Listening on port \${PORT}\`
        : 'Server is not listening',
    };
  }

  private scheduleMidnightUpdate() {
    const now = new Date();

    // Schedule the next tick at local midnight so current_year rolls over in real time.
    const midnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1,
    );
    const msUntilMidnight = midnight.getTime() - now.getTime();

    this.midnightTimer = setTimeout(() => {
      this.siteInfo.current_year = new Date().getFullYear();
      this.scheduleMidnightUpdate();
    }, msUntilMidnight);
  }

  private registerRoutes(server: APIServer) {
    // ─── API routes (via server.api.* shortcuts) ─────────────────────────────
    //
    // Omit version to default to v1, or pass a number for v2+:
    // server.api.get('endpoint', handler);        → GET /api/v1/endpoint
    // server.api.get('endpoint', 2, handler);     → GET /api/v2/endpoint
    //
    // Available: .api.get, .api.post, .api.put, .api.delete, .api.patch
    //
    // Handler signature: (request, reply, params) => APIResponseEnvelope
    // params includes: method, endpoint, version, fullPath, routeParams,
    //                   queryParams, requestPath, originalURL, APIResponseHelpers
    //
    // These helpers require API handling to be enabled. If apiEndpointPrefix is
    // false for plain web server mode, use pluginHost.get/post routes from a
    // plugin instead.
    //
    // Important:
    // - Handlers must return a valid APIResponseEnvelope. The HTTP status code and content-type
    //   are managed by the framework using the envelope's status_code property.
    // - Do not call reply.send() — return the envelope directly. The second handler argument
    //   is a ControlledReply wrapper used for setting headers or cookies (when the cookies plugin
    //   is registered). It does not expose direct reply.send() to ensure the envelope pattern is respected.
    // - Use params.APIResponseHelpers (e.g., createAPISuccessResponse) to construct envelopes,
    //   ensuring custom helper subclasses configured on the server are respected.
    // - Redirects are UI concerns: (For example, standard HTTP-level redirects (3xx) are
    //   blocked on page data routes in favor of native redirect envelopes). For custom
    //   API endpoints (e.g., submitting a form or resource creation), do not return 3xx.
    //   Instead, return a standard success envelope (200/201) and include the target
    //   destination URI inside the JSON payload (e.g., \`data: { url: '/v1/items/123' }\`).
    //   This mirrors how page data loaders handle redirects within the application envelope
    //   and allows the frontend client/form handler to perform the navigation.
    //
    // See: https://github.com/keverw/unirend/blob/master/docs/ssr.md (Custom API Routes)

    // GET /api/v1/health
    // Production tip: In a real application, you can also inject the LifecycleManager
    // instance and check/poll the status of other components (e.g., database, cache)
    // via manager.checkAllHealth() to determine the overall API service health.
    // See: https://github.com/keverw/lifecycleion/blob/master/docs/lifecycle-manager.md
    server.api.get('health', (request, _reply, params) => {
      return params.APIResponseHelpers.createAPISuccessResponse({
        request,
        data: {
          healthy: true,
          timestamp: new Date().toISOString(),
          build: request.publicAppConfig?.build,
        },
        statusCode: 200,
      });
    });

    server.api.get('echo/:name', (request, _reply, params) => {
      return params.APIResponseHelpers.createAPISuccessResponse({
        request,
        data: {
          name: params.routeParams.name,
          query: params.queryParams,
          endpoint: params.fullPath,
          version: params.version,
        },
        statusCode: 200,
      });
    });

    server.api.post('echo', (request, _reply, params) => {
      return params.APIResponseHelpers.createAPISuccessResponse({
        request,
        data: {
          received: request.body,
          endpoint: params.fullPath,
          version: params.version,
        },
        statusCode: 200,
      });
    });

    // ─── Page data handler ───────────────────────────────────────────────────
    //
    // APIServer supports the same pageDataHandler.register() as SSRServer.
    // - Co-located (same process): During initial page render, SSR data loaders short-circuit
    //   and call handlers directly (no HTTP overhead). After hydration, browser-side data loaders
    //   fetch them over HTTP from the same server during client-side navigation.
    // - Standalone (separated SSR/API): SSR data loaders fetch them from this server
    //   over HTTP during server-side rendering, while browser-side data loaders fetch
    //   them over HTTP (using the configured API endpoint URL) during client-side navigation.
    //
    // Omit version to default to v1, or pass a number for v2+:
    // server.pageDataHandler.register('page-type', handler);        → POST /api/v1/page_data/page-type
    // server.pageDataHandler.register('page-type', 2, handler);     → POST /api/v2/page_data/page-type
    //
    // See: https://github.com/keverw/unirend/blob/master/docs/data-loaders.md

    server.pageDataHandler.register('demo', (request, _reply, params) => {
      return params.APIResponseHelpers.createPageSuccessResponse({
        request,
        data: {
          message: 'Demo page data from the API server',
          timestamp: new Date().toISOString(),
          invocationOrigin: params.invocationOrigin,
        },
        pageMetadata: {
          title: 'Demo - API Server',
          description: 'Demo page data served by the standalone API server',
        },
      });
    });
  }
}
`;
}

/**
 * Ensure an API app's `api-component.ts` exists at `${projectPath}/api-component.ts`.
 * Only creates the file if it doesn't exist - never overwrites.
 *
 * @param root - File root (filesystem path or in-memory object)
 * @param projectPath - Relative path to the project directory (e.g. "src/apps/my-api")
 * @param appName - The app/project name, used in the build-script comment
 * @param log - Optional logger function for output
 * @throws {Error} If file creation fails
 */
export async function ensureAPIComponent(
  root: FileRoot,
  projectPath: string,
  appName: string,
  log?: LoggerFunction,
): Promise<void> {
  const relPath = `${projectPath}/api-component.ts`;

  try {
    const didWrite = await vfsWriteIfNotExists(
      root,
      relPath,
      buildAPIComponentSrc(appName),
    );

    if (didWrite && log) {
      log('info', `Created ${relPath}`);
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to ensure ${relPath}: ${errorMessage}`);
  }
}
